'use client';

import { c as _c } from "react/compiler-runtime";
import { AIFI_LEVEL_COLORS, AIFI_LEVEL_ICONS, AIFI_ORG_COLOR } from '@/domains/pages/ai-proficiency/constants';
import { pieChartAccentStyle, POWER_BAR_HEIGHT_PX } from '@/domains/pages/shared/power-bar-shared';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import type { PromotionTransition } from '@/domains/pages/ai-proficiency/utils/upskilling';
import { formatUserCount } from '@/domains/pages/ai-proficiency/utils/format-users';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { ArrowUp } from 'lucide-react';
type PromotionPotentialSectionProps = {
  transitions: PromotionTransition[];
  totalCloseToPromotion: number;
  scopeDescription: string;
  className?: string;
};
export function PromotionPotentialSection(t0) {
  const $ = _c(20);
  const {
    transitions,
    totalCloseToPromotion,
    scopeDescription,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowUp className="size-4 text-muted-foreground" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("font-semibold", AIFI_DATA_NUMBER_CLASS);
    t4 = {
      color: AIFI_ORG_COLOR
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== totalCloseToPromotion) {
    t5 = formatUserCount(totalCloseToPromotion);
    $[5] = totalCloseToPromotion;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <span className={t3} style={t4}>{t5}</span>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== scopeDescription || $[10] !== t6) {
    t7 = <div className="border-b border-b-lightborder px-8 py-4"><div className="flex items-center gap-2">{t2}<p className="text-sm text-muted-foreground">{scopeDescription}{" "}{t6}{" "}close to promotion.</p></div></div>;
    $[9] = scopeDescription;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== transitions) {
    t8 = transitions.map(_temp);
    $[12] = transitions;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <div className="grid grid-cols-3 divide-x divide-lightborder">{t8}</div>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t1 || $[17] !== t7 || $[18] !== t9) {
    t10 = <div className={t1} data-testid="promotion-potential-section">{t7}{t9}</div>;
    $[16] = t1;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
function _temp(transition) {
  const fromColor = AIFI_LEVEL_COLORS[transition.fromLevel];
  const toColor = AIFI_LEVEL_COLORS[transition.toLevel];
  const FromIcon = AIFI_LEVEL_ICONS[transition.fromLevel];
  const ToIcon = AIFI_LEVEL_ICONS[transition.toLevel];
  const barWidth = Math.min(100, Math.max(0, transition.percentOfLevel));
  return <div key={transition.fromLevel} className="flex min-w-0 flex-col px-8 py-5" data-testid={`promotion-transition-${transition.fromLevel}`}><div className="flex items-center gap-1.5 text-sm"><FromIcon className="size-3.5 shrink-0" style={{
        color: fromColor
      }} strokeWidth={2.25} /><span className="text-foreground">{transition.fromLevel}</span><span className="text-muted-foreground">→</span><ToIcon className="size-3.5 shrink-0" style={{
        color: toColor
      }} strokeWidth={2.25} /><span style={{
        color: toColor
      }}>{transition.toLevel}</span></div><div className="mt-3 flex flex-wrap items-baseline gap-x-1.5 gap-y-1"><span className={cn("text-2xl font-semibold text-foreground", AIFI_DATA_NUMBER_CLASS)}>{formatUserCount(transition.count)}</span><span className="text-xs text-foreground">of <span className={AIFI_DATA_NUMBER_CLASS}>{formatUserCount(transition.levelTotal)}</span></span><span className={cn("text-xs text-muted-foreground", AIFI_DATA_NUMBER_CLASS)}>{formatScore(transition.percentOfLevel, {
          decimals: 0
        })}% of the level</span></div><div className="mt-4 w-full overflow-hidden bg-muted/30" style={{
      height: POWER_BAR_HEIGHT_PX
    }}><div className="h-full transition-all" style={{
        width: `${barWidth}%`,
        ...pieChartAccentStyle(toColor)
      }} /></div></div>;
}